import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAgent from '../../hooks/useAgent';
import useAppStore from '../../store';
import AgentHeader from './AgentHeader';
import AgentControls from './AgentControls';
import AgentChatPanel from './AgentChatPanel';
import LiveActivityTab from './tabs/LiveActivityTab';
import TaskHistoryTab from './tabs/TaskHistoryTab';
import MemoryTab from './tabs/MemoryTab';
import ConfigurationTab from './tabs/ConfigurationTab';
import PerformanceTab from './tabs/PerformanceTab';

const TABS = [
  { id: 'live', label: 'Live activity' },
  { id: 'tasks', label: 'Task history' },
  { id: 'memory', label: 'Memory' },
  { id: 'config', label: 'Configuration' },
  { id: 'performance', label: 'Performance' }
];

// T6.1 — Agent Detail container. Reads :id, restores state on deep-link
// refresh (T6.13): the store re-seeds from mocks and each tab hook
// fetches/subscribes on mount, so a refresh on /agents/:id is fully rebuilt.
function AgentDetailPage() {
  const { id } = useParams();
  const { agent, agentMode, isLoading } = useAgent(id);
  const agents = useAppStore((state) => state.agents);
  const [activeTab, setActiveTab] = useState('live');
  const tabListRef = useRef(null);

  if (!isLoading && !agent) {
    return (
      <main className="page-content">
        <div className="empty-state">
          <h1>Agent not found</h1>
          <p>No agent with id “{id}”. Known agents: {agents.map((entry) => entry.name).join(', ')}.</p>
        </div>
      </main>
    );
  }

  if (!agent) {
    return <main className="page-content"><p className="tab-empty">Loading agent…</p></main>;
  }

  const handleTabKeyDown = (event) => {
    const currentIndex = TABS.findIndex((tab) => tab.id === activeTab);

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (currentIndex + delta + TABS.length) % TABS.length;
      setActiveTab(TABS[nextIndex].id);
      tabListRef.current?.children[nextIndex]?.focus();
    }
  };

  return (
    <main className="page-content agent-detail">
      <AgentHeader agent={agent} mode={agentMode}>
        <AgentControls agent={agent} />
      </AgentHeader>

      <div
        aria-label={`${agent.name} detail tabs`}
        aria-orientation="horizontal"
        className="tab-strip"
        onKeyDown={handleTabKeyDown}
        ref={tabListRef}
        role="tablist"
      >
        {TABS.map((tab) => (
          <button
            aria-selected={activeTab === tab.id}
            className={`tab-item ${activeTab === tab.id ? 'is-active' : ''}`}
            id={`tab-${tab.id}`}
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            role="tab"
            tabIndex={activeTab === tab.id ? 0 : -1}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        aria-labelledby={`tab-${activeTab}`}
        className="tab-panel"
        role="tabpanel"
      >
        {activeTab === 'live' && <LiveActivityTab agent={agent} />}
        {activeTab === 'tasks' && <TaskHistoryTab agent={agent} />}
        {activeTab === 'memory' && <MemoryTab agent={agent} />}
        {activeTab === 'config' && <ConfigurationTab agent={agent} />}
        {activeTab === 'performance' && <PerformanceTab agent={agent} />}
      </div>

      <AgentChatPanel agent={agent} />
    </main>
  );
}

export default AgentDetailPage;
